// Container de la sección "Gestión > Informes" en Monitor.
// Dos pestañas:
//   1. Configuración — frecuencias, destinatarios por tipo, cierre automático.
//   2. Solicitar ahora — envío inmediato con rango de fechas.
//
// Gate: se requiere `canEditTariffs` (flag general de Gestión). Si el usuario
// no lo tiene, el sidebar ya oculta el item (ver Sidebar.js), pero por
// defensa mostramos un mensaje read-only si entra directo a la URL.

import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { Container, Card, CardBody, Row, Col, Nav, NavItem, NavLink } from "reactstrap";
import moment from "moment";
import "moment/locale/es";

import ConfigTab from "./ConfigTab";
import SolicitarTab from "./SolicitarTab";

const TABS = [
  { id: "config", label: "Configuración", icon: "tune" },
  { id: "solicitar", label: "Solicitar ahora", icon: "send" },
];

const Informes = ({ currentCompany, appConfig, canEditPermissions, reportEmails, reportJobs }) => {
  document.title = "Informes | Monitor";
  const [activeTab, setActiveTab] = useState("config");
  const machineKey = currentCompany?.id;

  const updatedAt = appConfig?.updatedAt;
  const updatedMoment = useMemo(
    () => (updatedAt ? moment(updatedAt) : null),
    [updatedAt]
  );

  if (!machineKey) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <span
                className="material-symbols-rounded d-block mb-2"
                style={{ fontSize: 48, color: "#ccc" }}
              >
                group
              </span>
              <h5 className="text-muted mb-0">Seleccioná un cliente primero</h5>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  if (!appConfig || !appConfig.email_config) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <span
                className="material-symbols-rounded d-block mb-2"
                style={{ fontSize: 48, color: "#ccc" }}
              >
                sync_disabled
              </span>
              <h5 className="text-muted mb-0">
                Este cliente aún no sincronizó su configuración de informes
              </h5>
              <p className="text-muted small mt-2">
                Pedile que abra TicketControl y espere a la sincronización
                automática. Si el cliente corre una versión anterior, esta
                sección no está disponible hasta que actualice.
              </p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0">Informes</h4>
            <p className="text-muted small mb-0">
              Configurá los envíos automáticos y solicitá informes cuando los necesites.
            </p>
          </Col>
          <Col xs="auto">
            {updatedMoment && (
              <div className="text-end small text-muted">
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 16 }}
                >
                  update
                </span>
                Última sincronización: <b>{updatedMoment.locale("es").fromNow()}</b>
                <div style={{ fontSize: 11 }}>
                  {updatedMoment.format("DD/MM/YYYY HH:mm")}
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Banner experimental */}
        <Card
          className="mb-3"
          style={{
            borderLeft: "4px solid #50a5f1",
            backgroundColor: "rgba(80, 165, 241, 0.06)",
          }}
        >
          <CardBody className="py-2 px-3">
            <div className="d-flex align-items-start gap-2">
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 20, color: "#50a5f1" }}
              >
                science
              </span>
              <div className="small" style={{ color: "#1f4e79" }}>
                <b>Funcionalidad en pruebas.</b> Los cambios se envían en vivo a la
                máquina del estacionamiento. Si encontrás algo raro, contactanos.
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="mb-3">
          <Nav tabs className="px-3 pt-2" style={{ borderBottom: "1px solid #eee" }}>
            {TABS.map((tab) => (
              <NavItem key={tab.id}>
                <NavLink
                  className={activeTab === tab.id ? "active" : ""}
                  onClick={() => setActiveTab(tab.id)}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
                    {tab.icon}
                  </span>
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          <CardBody className="pt-3">
            {activeTab === "config" && (
              <ConfigTab
                machineKey={machineKey}
                appConfig={appConfig}
                reportEmails={reportEmails || []}
                canEdit={!!canEditPermissions}
              />
            )}
            {activeTab === "solicitar" && (
              <SolicitarTab
                machineKey={machineKey}
                reportEmails={reportEmails || []}
                canEdit={!!canEditPermissions}
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.companies.currentCompany,
  appConfig: state.data.appConfig,
  canEditPermissions: state.data.canEditTariffs, // mismo flag general de Gestión
  reportEmails: state.data.reportEmails,
  reportJobs: state.data.reportJobs,
});

export default connect(mapStateToProps)(Informes);
